/**
 * Karte „Account löschen" (Phase HH, Kapitel 20.6, Entscheidung #43).
 *
 * Sichtbar für Mitarbeiter UND Filialleitung — bewusst NICHT für den Chef
 * (alleiniger Wiederherstellungs-Anker für die Lohndaten der ganzen Firma,
 * siehe Konzept-Begründung; ein Firmeninhaber-Wechsel ist ein eigenständiges
 * Thema).
 *
 * Zwei getrennte Aktionen:
 *   - Datenauszug herunterladen (Art. 20 DSGVO) — jederzeit möglich, auch
 *     ohne Löschabsicht.
 *   - Account löschen — setzt den Löschantrag; der Account wird SOFORT
 *     inaktiv (identisch zur normalen Deaktivierung, Kapitel 20.3) und nach
 *     der firmenspezifischen Frist (Default 30 Tage) endgültig anonymisiert.
 *     Da die Person sich danach nicht mehr einloggen kann, loggt die App
 *     nach erfolgreicher Bestätigung sofort selbst aus.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/auth';
import { accountDeletionApi } from '../lib/accountDeletion';
import { ApiError } from '../lib/api';

function triggerJsonDownload(data: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function AccountDeletionCard() {
  const token = useAuth((s) => s.token);
  const logout = useAuth((s) => s.logout);
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!token) return null;

  async function onExport() {
    setError(null);
    setExporting(true);
    try {
      const data = await accountDeletionApi.dataExport(token!);
      triggerJsonDownload(data, `shiftsync-export-${new Date().toISOString().slice(0, 10)}.json`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Export fehlgeschlagen — bitte erneut versuchen.');
    } finally {
      setExporting(false);
    }
  }

  async function onDelete() {
    if (
      !confirm(
        'Dein Account wird in 30 Tagen endgültig gelöscht (die genaue Frist zeigt dir deine Firma). ' +
          'Bis dahin kannst du das jederzeit rückgängig machen, indem du dich bei deiner Filialleitung ' +
          'oder deinem Chef meldest.\n\nWICHTIG: Ab jetzt kannst du dich nicht mehr anmelden — lade ' +
          'deinen Datenauszug also VORHER herunter, falls du ihn brauchst.\n\nJetzt Löschung beantragen?',
      )
    ) {
      return;
    }
    setError(null);
    setDeleting(true);
    try {
      await accountDeletionApi.requestDeletion(token!);
      alert('Löschung beantragt. Du wirst jetzt abgemeldet.');
      logout();
      navigate('/login', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Löschantrag fehlgeschlagen — bitte erneut versuchen.');
      setDeleting(false);
    }
  }

  return (
    <div className="card p-4 space-y-3">
      <p className="text-sm font-medium">Account löschen</p>
      <p className="text-xs text-slate-400">
        Datenauszug herunterladen oder deinen Account endgültig löschen lassen. Nach dem Löschantrag
        ist keine Anmeldung mehr möglich — den Datenauszug bitte vorher herunterladen.
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => void onExport()}
          disabled={exporting}
          className="btn-ghost text-xs px-3 py-1.5 disabled:opacity-50"
        >
          {exporting ? '…' : 'Datenauszug herunterladen'}
        </button>
        <button
          onClick={() => void onDelete()}
          disabled={deleting}
          className="text-xs px-3 py-1.5 rounded bg-red-600/80 hover:bg-red-600 disabled:opacity-50"
        >
          {deleting ? '…' : 'Account löschen'}
        </button>
      </div>
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
}
